import type { StreamingServicePhase, WorkspaceStatus } from '@/lib/types/workspace';

// ⚠️ LEGACY — workspace pods predating the sim's `/sim/status/stream`.
//
// Old workspace servers probe the sim's `/sim/status` themselves and
// nest the full sim payload + derived streaming status inside the
// `/workspace/status` snapshot (`sim.status_payload`, `streaming`).
// Modern pods leave `sim.status_payload` null *and* omit `streaming` — the
// browser reads the sim's status stream directly (see SimStatusProvider) —
// so this reader returns null for them and the sim-direct path takes over.
// Old pods include the `streaming` block even before `/sim/status` is
// reachable, so treat that as a legacy marker to avoid probing a
// `/sim/status/stream` endpoint the image does not have.
//
// This module exists only to keep older workspaces working during the
// rollout. Delete it once no workspace images predating
// `/sim/status/stream` remain in service.

/** The sim-status view extracted from an old pod's workspace snapshot. */
export interface LegacySimView {
  simStatus: WorkspaceStatus | null;
  simReachable: boolean;
  streamingStatus: StreamingServicePhase;
}

/** Minimal shape of the legacy fields on a workspace status snapshot. */
export interface LegacyWorkspaceSnapshot {
  sim?: { reachable?: boolean; status_payload?: WorkspaceStatus | null };
  streaming?: { status?: StreamingServicePhase };
}

/**
 * Extract sim status from an old-pod workspace snapshot.
 *
 * :param snapshot: The raw workspace status snapshot, or null.
 * :return: The legacy sim view when the snapshot carries a nested sim
 *     payload (old pod), or null when it does not (modern pod, or no
 *     snapshot) — signalling the caller to use the sim-direct stream.
 */
export function readLegacySimStatus(snapshot: LegacyWorkspaceSnapshot | null): LegacySimView | null {
  const payload = snapshot?.sim?.status_payload ?? null;
  const streamingStatus = snapshot?.streaming?.status;
  if (payload == null && streamingStatus == null) return null;
  return {
    simStatus: payload,
    simReachable: payload !== null && (snapshot?.sim?.reachable ?? false),
    streamingStatus: streamingStatus ?? 'unknown',
  };
}
